import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

import {useQueryClient} from "@tanstack/react-query";

import {Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle} from "@/components/ui/sheet";
import {useStoredMe} from "@/src/modules/core/store/me";

import {useNotificationActions} from "../hooks/use-notification-actions";
import {useNotificationFeed} from "../hooks/use-notification-feed";
import {
  getNotificationBellQueryParams,
  isAlertsBellTopic,
  isResidentBellTopic,
} from "../lib/notification-feed-topics";
import {openNotificationSubject} from "../lib/open-notification-subject";
import {dedupeNotificationsForTriage} from "../lib/dedupe-triage-notifications";
import {NotificationFeedRow} from "./notification-feed-row";
import {NotificationEnablePushBanner} from "./notification-enable-push-banner";

type NotificationsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function NotificationsSheet({open, onOpenChange}: NotificationsSheetProps) {
  const {t} = useTranslation("common");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useStoredMe();
  const isResident = Boolean(me?.isResident);
  const bellQueryParams = getNotificationBellQueryParams(isResident);
  const {items, isPending} = useNotificationFeed({
    params: bellQueryParams,
    enabled: open,
  });
  const {markReadByIds, discardByIds, isDiscardPending, isMarkReadPending} = useNotificationActions();

  const unreadItems = useMemo(
    () =>
      dedupeNotificationsForTriage(
        items.filter((item) => {
          if (item.read_at || item.discarded_at) {
            return false;
          }
          return isResident ? isResidentBellTopic(item.topic) : isAlertsBellTopic(item.topic);
        }),
      ),
    [items, isResident],
  );
  const hasAnyUnread = unreadItems.length > 0;

  return (
    <Sheet open={open} modal={false} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg">
        <SheetHeader className="pb-2">
          <SheetTitle>Notifications</SheetTitle>
          <SheetDescription>
            {isResident
              ? "Messages and updates about your household."
              : "Quick triage for shift, vehicle, and briefing alerts."}
          </SheetDescription>
        </SheetHeader>
        <NotificationEnablePushBanner />
        <div className="flex flex-1 flex-col gap-3 overflow-hidden px-4 pb-4">
          <div className="flex flex-col gap-2 overflow-y-auto pr-1">
            {isPending ? (
              <div className="text-sm text-muted-foreground">{t("loading")}</div>
            ) : hasAnyUnread ? (
              unreadItems.map((item) => (
                <NotificationFeedRow
                  key={item.id}
                  item={item}
                  isMarkReadPending={isMarkReadPending}
                  isDismissPending={isDiscardPending}
                  onMarkRead={(currentItem) => {
                    markReadByIds([currentItem.id]);
                  }}
                  onDismiss={(id) => discardByIds([id])}
                  onOpenSubject={(item, path) => {
                    void openNotificationSubject(item, path, navigate, queryClient).then(() => {
                      onOpenChange(false);
                    });
                  }}
                />
              ))
            ) : (
              <div className="bg-card rounded-lg border p-4 text-sm text-muted-foreground">
                {isResident ? "You have no unread notifications." : "You have no unread alerts."}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
